import { z } from 'zod';
import {
  PAGINATION_PAGE_SIZE_DEFAULT,
  PAGINATION_SORT_DIRECTION,
} from '~/constants';

import { zodPollerErrorTrackingSchema } from '~/data/poller-error-tracking';

import {
  JsonSchema,
  toSnakeCase,
  withoutObjectKeysWhereValueIs,
} from '@vestigas/utils';

import { FTP_POLLERS_ORDER_BY } from './constants';
import { FTP_PROTOCOL } from './useMutationCreateFtpPoller';

const zodFtpPollersFilterModelSchema = z.object({
  buyerCompanyAccountId: z.uuid().optional(),
  buyerId: z.uuid().optional(),
  isActive: z.boolean().nullable().optional(),
  issuerCompanyAccountId: z.uuid().optional(),
  issuerId: z.uuid().optional(),
  pollingProblem: z.boolean().nullable().optional(),
  searchString: z.string().trim().prefault(''),
});

export const zodFtpPollersQueryParamsSchema = zodFtpPollersFilterModelSchema
  .extend({
    limit: z
      .number()
      .gte(1)
      .max(100)
      .optional()
      .prefault(PAGINATION_PAGE_SIZE_DEFAULT),
    offset: z.number().nonnegative().optional().prefault(0),
    orderBy: z
      .preprocess(
        (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
        z.enum(FTP_POLLERS_ORDER_BY),
      )
      .optional()
      .prefault('last_run'),
    sort: z
      .preprocess(
        (value) => String(value).toUpperCase(),
        z.enum(PAGINATION_SORT_DIRECTION),
      )
      .optional()
      .prefault('DESC'),
  })
  .transform((value) =>
    withoutObjectKeysWhereValueIs(value, [null, undefined, '']),
  );

export const zodFtpPollerSchema = zodPollerErrorTrackingSchema.extend({
  batchSize: z.number().int().min(1),
  buyerCompany: z.string().nullable(),
  buyerCompanyAccountId: z.uuid().nullable(),
  buyerCompanyAccountName: z.string().nullable(),
  buyerId: z.uuid().nullable(),
  confirmLargeBatch: z.boolean().optional().default(false),
  contextData: z.record(z.string(), JsonSchema).nullable(),
  createdById: z.uuid().nullable(),
  // Unlike the shared error-tracking timestamps, these three are plain Pydantic
  // `datetime` fields on a timezone-aware column, so the API can emit an offset
  // (`+02:00`) instead of `Z` depending on its session timezone. Accept both.
  createdOn: z.iso.datetime({ offset: true }),
  deleteSource: z.boolean(),
  extremeBatchThreshold: z.number().int().min(1),
  fileType: z.string(),
  id: z.uuid(),
  isActive: z.boolean().nullable(),
  issuerCompany: z.string().nullable(),
  issuerCompanyAccountId: z.uuid().nullable(),
  issuerCompanyAccountName: z.string().nullable(),
  issuerId: z.uuid(),
  largeBatchThreshold: z.number().int().min(1),
  lastReceivedOn: z.iso.datetime({ offset: true }).nullable(),
  maxFileAgeDays: z.number().int().min(1).nullable(),
  modifiedById: z.uuid().nullable(),
  modifiedOn: z.iso.datetime({ offset: true }),
  password: z.string(),
  path: z.string(),
  pendingFileCount: z.number().int().nonnegative().nullable(),
  port: z.number().int().min(1).max(65_535),
  // The API stores this in an unconstrained string column, so an unexpected
  // legacy value must not invalidate the whole list response — fall back to null.
  protocol: z.enum(FTP_PROTOCOL).nullable().catch(null),
  server: z.string(),
  username: z.string(),
});

export const zodFtpPollersResponseSchema = z.object({
  data: z.array(zodFtpPollerSchema),
  limit: z.number().int().nonnegative(),
  next: z.string().nullable(),
  offset: z.number().int().nonnegative(),
  previous: z.string().nullable(),
  total: z.number().int().nonnegative(),
});
